import React, { useEffect, useRef, useState } from 'react';
import SkeletonComponent from '../SkeletonComponent';

const VideoHandler = ({
    src,
    title = 'Video',
    className = '',
    preloadOnView = true,
    rootMargin = '200px',
    poster,
    muted = false,
    autoPlay = false,
    loop = false,
    playsInline = false,
    addSkeleton = true,
    showControls = true,
    showPlayButton = true,
}) => {
    const wrapperRef = useRef(null);
    const videoRef = useRef(null);
    const [canLoad, setCanLoad] = useState(!preloadOnView);
    const [isPlaying, setIsPlaying] = useState(false);
    const [posterLoaded, setPosterLoaded] = useState(false);

    useEffect(() => {
        if (!preloadOnView || !wrapperRef.current || typeof IntersectionObserver === 'undefined') return;
        const io = new IntersectionObserver((entries) => {
            if (entries.some(e => e.isIntersecting)) { handlePlay(); io.disconnect(); }
        }, { rootMargin });
        io.observe(wrapperRef.current);
        return () => io.disconnect();
    }, [preloadOnView, rootMargin]);

    const handlePlay = () => {
        setCanLoad(true);
        setIsPlaying(true);

        requestAnimationFrame(() => {
            const v = videoRef.current;
            if (v) v.play().catch(() => { /* ignore autoplay promise errors */ });
        });
    };

    const showSkeleton = addSkeleton && (!posterLoaded && !isPlaying);

    return (
        <div ref={wrapperRef} className={`video ${className} ${isPlaying ? 'video--playing' : ''}`} aria-label={title}>
            {showSkeleton && (
                <div className="video__skeleton" aria-hidden="true">
                    <SkeletonComponent numberOfItems={1} SkeletonClassName="video__skel" />
                </div>
            )}

            {!isPlaying && poster && (
                <img
                    className="video__poster"
                    src={poster}
                    alt={title}
                    loading="lazy"
                    onLoad={() => setPosterLoaded(true)}
                    onError={() => setPosterLoaded(true)}
                    onClick={handlePlay}
                />
            )}

            {(canLoad || isPlaying) && (
                <video
                    ref={videoRef}
                    className="video__el"
                    title={title}
                    controls={showControls}
                    playsInline={playsInline}
                    preload="metadata"
                    poster={poster}
                    autoPlay={isPlaying || autoPlay}
                    loop={loop}
                    muted={muted}
                    onLoadedData={() => { if (isPlaying) videoRef.current?.play().catch(() => { /* ignore autoplay promise errors */ }); }}
                >
                    <source src={src} type="video/mp4" />
					Your browser does not support the video tag.
                </video>
            )}

            {!isPlaying && (
                showPlayButton && <>
                    <div className="video__backdrop" aria-hidden="true" onClick={handlePlay}></div>
                    <button type="button" className="video__button" aria-label={`Play: ${title}`} onClick={handlePlay}>
                        <span className="video__triangle" aria-hidden="true"></span>
                    </button>
                </>
            )}
        </div>
    );
};

export default VideoHandler;
